import { api } from './api';
import type { AxiosResponse, AxiosError } from 'axios';
import type {
  LoginCredentials,
  RegisterData,
  AuthResponse,
  UserProfile,
  UpdateProfileData,
  ApiResponse,
} from '../types/auth';
import { tokenStorage } from '../utils/tokenStorage';
import { createEnhancedError } from '../utils/error-handling';

// Enhanced error handling for authentication-specific errors
const handleAuthError = (error: AxiosError): never => {
  // Create enhanced error with user-friendly messages and error codes
  throw createEnhancedError(error);
};

// Store tokens securely
const storeTokens = (
  authResponse: AuthResponse,
  rememberMe?: boolean,
): void => {
  const { accessToken, refreshToken, user } = authResponse;

  tokenStorage.setTokens({ accessToken, refreshToken }, rememberMe);
  tokenStorage.setUser(user);
};

// Clear stored tokens
const clearTokens = (): void => {
  tokenStorage.clearTokens();
};

// Authentication API service
export const authApi = {
  // User authentication
  login: async (
    credentials: LoginCredentials,
  ): Promise<AxiosResponse<ApiResponse<AuthResponse>>> => {
    try {
      const response = await api.post<AuthResponse>('/auth/login', credentials);

      // Store tokens on successful login
      if (response.data.success && response.data.data) {
        storeTokens(response.data.data);
      }

      return response;
    } catch (error) {
      return handleAuthError(error as AxiosError);
    }
  },

  register: async (
    userData: RegisterData,
  ): Promise<AxiosResponse<ApiResponse<AuthResponse>>> => {
    try {
      const response = await api.post<AuthResponse>('/auth/register', userData);

      // Store tokens on successful registration
      if (response.data.success && response.data.data) {
        storeTokens(response.data.data);
      }

      return response;
    } catch (error) {
      return handleAuthError(error as AxiosError);
    }
  },

  logout: async (): Promise<AxiosResponse<ApiResponse<void>>> => {
    try {
      const response = await api.post('/auth/logout');

      // Always clear local tokens on logout, regardless of API response
      clearTokens();

      return response as AxiosResponse<ApiResponse<void>>;
    } catch (error) {
      // Don't throw on logout errors - always allow local logout
      console.warn('Logout API call failed:', error);

      // Clear tokens even if API call fails
      clearTokens();

      return {
        data: { success: true, message: 'Logged out locally' },
        status: 200,
        statusText: 'OK',
        headers: {},
        config: {} as any,
      };
    }
  },

  // Token management
  refreshToken: async (
    refreshToken: string,
  ): Promise<AxiosResponse<ApiResponse<AuthResponse>>> => {
    try {
      const response = await api.post<AuthResponse>('/auth/refresh', {
        refreshToken,
      });

      // Update stored tokens on successful refresh
      if (response.data.success && response.data.data) {
        storeTokens(response.data.data);
      }

      return response;
    } catch (error) {
      // Clear tokens on refresh failure
      clearTokens();
      return handleAuthError(error as AxiosError);
    }
  },

  // User profile
  getProfile: async (): Promise<AxiosResponse<ApiResponse<UserProfile>>> => {
    try {
      return await api.get<UserProfile>('/auth/profile');
    } catch (error) {
      return handleAuthError(error as AxiosError);
    }
  },

  updateProfile: async (
    data: UpdateProfileData,
  ): Promise<AxiosResponse<ApiResponse<UserProfile>>> => {
    try {
      const response = await api.put<UserProfile>('/auth/profile', data);

      // Update stored user data if profile update was successful
      if (response.data.success && response.data.data) {
        tokenStorage.setUser(response.data.data);
      }

      return response;
    } catch (error) {
      return handleAuthError(error as AxiosError);
    }
  },

  // Password management
  forgotPassword: async (
    email: string,
  ): Promise<AxiosResponse<ApiResponse<void>>> => {
    try {
      const response = await api.post('/auth/forgot-password', { email });

      // Backend returns 204 No Content, so we need to create a proper response structure
      if (response.status === 204) {
        return {
          ...response,
          data: { success: true, message: 'Password reset instructions sent' },
        } as AxiosResponse<ApiResponse<void>>;
      }

      return response as AxiosResponse<ApiResponse<void>>;
    } catch (error) {
      return handleAuthError(error as AxiosError);
    }
  },

  resetPassword: async (
    token: string,
    newPassword: string,
  ): Promise<AxiosResponse<ApiResponse<void>>> => {
    try {
      const response = await api.post('/auth/reset-password', {
        token,
        newPassword,
      });

      // Backend returns 204 No Content, so we need to create a proper response structure
      if (response.status === 204) {
        // Clear tokens after successful password reset to force re-login
        clearTokens();

        return {
          ...response,
          data: { success: true, message: 'Password reset successfully' },
        } as AxiosResponse<ApiResponse<void>>;
      }

      // Clear tokens after successful password reset to force re-login
      if (response.data.success) {
        clearTokens();
      }

      return response as AxiosResponse<ApiResponse<void>>;
    } catch (error) {
      return handleAuthError(error as AxiosError);
    }
  },

  changePassword: async (
    currentPassword: string,
    newPassword: string,
  ): Promise<AxiosResponse<ApiResponse<void>>> => {
    try {
      const response = await api.post('/auth/change-password', {
        currentPassword,
        newPassword,
      });

      // Optionally clear tokens after password change to force re-login for security
      // This is a security best practice but can be disabled for better UX
      if (response.data.success) {
        // Uncomment the next line if you want to force re-login after password change
        // clearTokens();
      }

      return response as AxiosResponse<ApiResponse<void>>;
    } catch (error) {
      return handleAuthError(error as AxiosError);
    }
  },

  activate: async (
    token: string,
  ): Promise<AxiosResponse<ApiResponse<AuthResponse>>> => {
    try {
      const response = await api.post<AuthResponse>('/auth/activate', {
        token,
      });

      // Store tokens on successful activation
      if (response.data.success && response.data.data) {
        storeTokens(response.data.data);
      }

      return response;
    } catch (error) {
      return handleAuthError(error as AxiosError);
    }
  },

  resendActivation: async (): Promise<AxiosResponse<ApiResponse<void>>> => {
    try {
      const response = await api.post('/auth/resend-activation');

      if (response.status === 204) {
        return {
          ...response,
          data: {
            success: true,
            message: 'Activation email resent successfully',
          },
        } as AxiosResponse<ApiResponse<void>>;
      }

      return response as AxiosResponse<ApiResponse<void>>;
    } catch (error) {
      return handleAuthError(error as AxiosError);
    }
  },

  // Utility methods
  isAuthenticated: (): boolean => {
    return tokenStorage.isAuthenticated();
  },

  getCurrentUser: (): UserProfile | null => {
    return tokenStorage.getUser();
  },

  getStoredTokens: (): {
    accessToken: string | null;
    refreshToken: string | null;
  } => {
    return {
      accessToken: tokenStorage.getAccessToken(),
      refreshToken: tokenStorage.getRefreshToken(),
    };
  },

  clearStoredData: (): void => {
    clearTokens();
  },
};
